import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import useTranslation from "../../client/hooks/useTranslation";
import type { LiteAccount } from "../data/LiteDataProvider";
import { useLiteData } from "../data/LiteDataProvider";
import { AccountProgressBar } from "../../trading-accounts/components/AccountProgressBar";
import { CURRENCY } from "../../shared/currency";
import { FirmMark, Pnl, StatusBadge, cardCls, type Tone } from "./ui";

/** Ton de la marge : rouge sous 25 %, ambre sous 50 %, sinon vert ; le verdict du moteur prime. */
export function cushionTone(c: LiteAccount["cushion"]): Tone {
  if (!c) return "gain";
  if (c.violation) return "loss";
  if (c.warning) return "warn";
  return c.ratio < 0.25 ? "loss" : c.ratio < 0.5 ? "warn" : "gain";
}

/** L'axe plancher -> cible de la carte Advanced, réutilisé tel quel. */
export function AccountAxis({ account }: { account: LiteAccount }) {
  if (!account.axis) return null;
  return <AccountProgressBar bounds={account.axis.bounds} currentBalance={account.balance} currency={CURRENCY} tone={account.axis.tone} size="sm" />;
}

/** Ligne compacte (tableau de bord). */
export function AccountMini({ account }: { account: LiteAccount }) {
  const { fmt } = useLiteData();
  return (
    <Link to={`/account/${account.id}`} className="block rounded-xl px-3 py-3 -mx-3 hover:bg-muted/60 transition-colors">
      <div className="flex items-center gap-3">
        <FirmMark firm={account.firm} size="sm" />
        <div className="min-w-0 flex-1 text-sm font-medium truncate">{account.name}</div>
        <div className="font-mono text-sm">{fmt.money(account.balance, { sign: false })}</div>
      </div>
      {account.axis && <div className="mt-2.5 pl-9"><AccountAxis account={account} /></div>}
    </Link>
  );
}

/** Carte complète (page Comptes). */
export function AccountCard({ account }: { account: LiteAccount }) {
  const t = useTranslation().lite.account;
  const { fmt } = useLiteData();
  const roi = account.status === "funded" && account.expensesTotal ? account.net / account.expensesTotal : null;
  return (
    <Link to={`/account/${account.id}`} className={`${cardCls} p-5 flex flex-col gap-4 hover:border-primary/40 transition-colors group ${account.active ? "" : "opacity-70"}`}>
      <div className="flex items-start gap-3">
        <FirmMark firm={account.firm} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{account.name}</div>
          {account.firm && <div className="text-xs text-muted-foreground/70 truncate">{account.firm.name}</div>}
        </div>
        <ChevronRight size={18} className="text-muted-foreground/70 group-hover:text-primary transition-colors" />
      </div>
      <div>
        <StatusBadge status={account.status} />
        <div className="flex items-baseline justify-between gap-3 mt-3">
          <div className="font-mono text-2xl font-semibold">{fmt.money(account.balance, { sign: false })}</div>
          <Pnl value={account.pnl} className="text-sm" />
        </div>
      </div>
      <div className="space-y-3">
        <AccountAxis account={account} />
        {account.status === "breached" && <p className="text-xs text-pnlNegative">{t.breached}</p>}
        {account.status === "inactive" && <p className="text-xs text-muted-foreground">{t.inactive}</p>}
      </div>
      {account.firm && (
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border text-xs">
          <div><div className="text-muted-foreground/70">{t.spent}</div><div className="font-mono mt-0.5">{fmt.money(-account.expensesTotal)}</div></div>
          <div><div className="text-muted-foreground/70">{t.payouts}</div><div className="font-mono mt-0.5 text-pnlPositive">{fmt.money(account.payoutsTotal)}</div></div>
          <div><div className="text-muted-foreground/70">{t.roi}</div><div className={`font-mono mt-0.5 ${roi !== null && roi >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>{roi === null ? "-" : fmt.pct(roi)}</div></div>
        </div>
      )}
    </Link>
  );
}
